import React, { useState } from "react";
import { toast } from "react-toastify";
import CircularProgress from "@material-ui/core/CircularProgress";
import * as XLSX from "xlsx";
import Button from "../../../components/UI/Button/Button";
import IntervalEditForm, {
  PASSWORD_TYPES,
} from "../components/IntervalEditForm";
import ChangeReservationResults from "../components/ChangeReservationResults";
import {
  buildChangeReservationPayload,
  notifyChangeReservationOutcome,
  parseChangeReservationResponse,
  submitChangeOrderReservation,
} from "../utils/changeOrderReservation";
import { isEmptyTime } from "../../../components/UI/IntervalTimePicker/IntervalTimePicker";
import "../ChangeOrderInterval.css";

const emptyPasswordExpires = () => {
  const expires = {};
  PASSWORD_TYPES.forEach((type) => {
    expires[type] = { expireDate: "", expireTime: "" };
  });
  return expires;
};

const readExcelRowsAsText = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const workbook = XLSX.read(e.target.result, {
          type: "array",
          cellText: true,
          cellDates: false,
        });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        // raw:false keeps formatted/text values so leading zeros are preserved
        const rows = XLSX.utils.sheet_to_json(sheet, {
          header: 1,
          raw: false,
          defval: "",
          blankrows: false,
        });
        resolve(rows);
      } catch (error) {
        reject(error);
      }
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsArrayBuffer(file);
  });
};

const BulkOrder = () => {
  const [loading, setLoading] = useState(false);
  const [trackNumbers, setTrackNumbers] = useState([]);
  const [fileError, setFileError] = useState("");
  const [startDate, setStartDate] = useState("");
  const [startTime, setStartTime] = useState("");
  const [endDate, setEndDate] = useState("");
  const [endTime, setEndTime] = useState("");
  const [passwordExpires, setPasswordExpires] = useState(emptyPasswordExpires);
  const [invalidDate, setInvalidDate] = useState(false);
  const [invalidTime, setInvalidTime] = useState(false);
  const [submitOutcome, setSubmitOutcome] = useState(null);

  const applyExcelRows = (rows, name) => {
    setFileError("");
    setTrackNumbers([]);

    if (!!name && name !== "") {
      const extension = name.split(".").pop().toLowerCase();
      if (extension !== "xlsx" && extension !== "xls") {
        setFileError("فرمت فایل صحیح نیست! فقط فایل xlsx مجاز است.");
        return;
      }
    }

    if (!rows || rows.length < 2) {
      setFileError("فایل اکسل خالی است یا فرمت آن صحیح نیست.");
      return;
    }

    const numbers = [];
    rows.slice(1).forEach((row) => {
      const value = row[0] != null ? String(row[0]).trim() : "";
      if (value !== "") {
        numbers.push(value);
      }
    });

    if (numbers.length === 0) {
      setFileError("هیچ شناسه سفارش دهنده ای در فایل یافت نشد.");
      return;
    }

    setTrackNumbers(numbers);
    toast.success(`${numbers.length} شناسه سفارش دهنده از فایل خوانده شد`);
  };

  const handleExcelFileChange = (event) => {
    const file = event.target.files && event.target.files[0];
    event.target.value = "";
    if (!file) return;

    readExcelRowsAsText(file)
      .then((rows) => applyExcelRows(rows, file.name))
      .catch(() => {
        setFileError("خطا در خواندن فایل اکسل.");
        setTrackNumbers([]);
      });
  };

  const validateForm = () => {
    let valid = true;
    if (trackNumbers.length === 0) {
      valid = false;
      setFileError("لطفاً فایل اکسل حاوی شماره شناسه سفارش دهنده ها را بارگذاری کنید.");
    }
    if (!startDate || !endDate) {
      valid = false;
      setInvalidDate(true);
    }
    if (isEmptyTime(startTime) || isEmptyTime(endTime)) {
      valid = false;
      setInvalidTime(true);
    }
    return valid;
  };

  const submitBulkChange = () => {
    if (!validateForm()) {
      toast.error("لطفاً فیلدهای الزامی را تکمیل کنید");
      return;
    }

    setLoading(true);
    setSubmitOutcome(null);
    const payload = buildChangeReservationPayload({
      courierTrackNumbers: trackNumbers,
      startDate,
      startTime,
      endDate,
      endTime,
      passwordExpires,
      passwordTypes: PASSWORD_TYPES,
    });

    submitChangeOrderReservation(payload)
      .then((result) => {
        setLoading(false);
        const outcome = parseChangeReservationResponse(result);
        setSubmitOutcome(outcome);
        notifyChangeReservationOutcome(outcome, toast);
      })
      .catch(() => {
        setLoading(false);
        toast.error("خطا در ارتباط با سرور");
      });
  };

  const onPasswordExpireDateChange = (type, formatted) => {
    setPasswordExpires((prev) => ({
      ...prev,
      [type]: { ...prev[type], expireDate: formatted },
    }));
  };

  const onPasswordExpireTimeChange = (type, value) => {
    setPasswordExpires((prev) => ({
      ...prev,
      [type]: { ...prev[type], expireTime: value },
    }));
  };

  return (
    <div className="change-interval-cntnr">
      <div className="change-interval-excel">
        <div className="change-interval-excel-header">
          <span className="change-interval-excel-badge">اکسل</span>
          <div>
            <h4 className="change-interval-excel-title">بارگذاری فایل اکسل</h4>
            <p className="change-interval-excel-desc">
              فایل xlsx را انتخاب کنید. شناسه سفارش دهنده ها باید در ستون اول قرار
              گیرند.
            </p>
          </div>
        </div>

        <div className="change-interval-excel-body">
          <div className="change-interval-excel-upload">
            <input
              accept=".xlsx,.xls"
              type="file"
              onChange={handleExcelFileChange}
              style={{ padding: "10px" }}
            />
          </div>

          {fileError ? (
            <div className="change-interval-error">{fileError}</div>
          ) : (
            ""
          )}

          {trackNumbers.length > 0 ? (
            <div className="change-interval-track-list">
              <div className="change-interval-track-list-header">
                <strong>
                  شناسه سفارش دهنده های بارگذاری‌شده ({trackNumbers.length})
                </strong>
              </div>
              <ul className="change-interval-track-chips">
                {trackNumbers.map((num, index) => (
                  <li key={index}>{num}</li>
                ))}
              </ul>
            </div>
          ) : (
            ""
          )}
        </div>
      </div>

      <IntervalEditForm
        startDate={startDate}
        startTime={startTime}
        endDate={endDate}
        endTime={endTime}
        passwordExpires={passwordExpires}
        invalidDate={invalidDate}
        invalidTime={invalidTime}
        onStartDateChange={(unix, formatted) => setStartDate(formatted)}
        onStartTimeChange={setStartTime}
        onEndDateChange={(unix, formatted) => setEndDate(formatted)}
        onEndTimeChange={setEndTime}
        onPasswordExpireDateChange={onPasswordExpireDateChange}
        onPasswordExpireTimeChange={onPasswordExpireTimeChange}
        headerText="مقادیر جدید برای همه سفارشات"
      />

      <div className="change-interval-submit-bar">
        <div className="change-interval-submit-info">
          {trackNumbers.length > 0
            ? `${trackNumbers.length} سفارش برای اعمال تغییرات آماده است`
            : "ابتدا فایل اکسل را بارگذاری کنید"}
        </div>
        <Button
          btnType="Success"
          className="change-interval-submit-btn"
          clicked={submitBulkChange}
          disabled={loading}
        >
          {loading ? (
            <CircularProgress size={18} className="change-interval-submit-spinner" />
          ) : (
            "ثبت تغییرات گروهی"
          )}
        </Button>
      </div>

      <ChangeReservationResults outcome={submitOutcome} />
    </div>
  );
};

export default BulkOrder;
